import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMutation, useManualQuery } from "graphql-hooks";
import {
  Box,
  Typography,
  Button,
  Avatar,
  Card,
  CardMedia,
  CardContent,
  Tooltip,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderOutlined from "@mui/icons-material/FavoriteBorderOutlined";
import ModeCommentOutlined from "@mui/icons-material/ModeCommentOutlined";
import ShareOutlined from "@mui/icons-material/ShareOutlined";
import CollectionsBookmarkOutlinedIcon from "@mui/icons-material/CollectionsBookmarkOutlined";
import CollectionsBookmarkIcon from "@mui/icons-material/CollectionsBookmark";
import FlagOutlined from "@mui/icons-material/FlagOutlined";
import FollowButton from "./components/FollowButton";
import FloatingMenu from "./components/FloatingMenu";
import ReportDialog from "./components/ReportDialog";
import CommentDialog from "./components/CommentDialog";
import UserCard from "./components/UserCard";
import SharePopup from "./components/SharePopup";
import AlertPopup from "./components/AlertPopup";
import dayjs from "dayjs";
import { isValidUUIDv4 } from "./hooks/uuid";

import {
  STORY_BY_UUID,
  CREATE_STORY_LIKE_MUTATION,
  DELETE_STORY_LIKE_MUTATION,
  UPDATE_STORY_SHARE_NO,
  CREATE_STORY_COMMENT_MUTATION,
  DELETE_STORY_COMMENT_MUTATION,
  EDIT_STORY_COMMENT_MUTATION,
  CREATE_STORY_COMMENT_LIKE_MUTATION,
  DELETE_STORY_COMMENT_LIKE_MUTATION,
  CREATE_STORY_COMMENT_REPLY_MUTATION,
  DELETE_STORY_COMMENT_REPLY_MUTATION,
  EDIT_STORY_COMMENT_REPLY_MUTATION,
  CREATE_STORY_COMMENT_REPLY_LIKE_MUTATION,
  DELETE_STORY_COMMENT_REPLY_LIKE_MUTATION,
} from "./graphQL/story_qry";

import {
  CREATE_FOLLOW_MUTATION,
  DELETE_FOLLOW_MUTATION,
} from "./graphQL/follow_qry";

import {
  CREATE_COLLECTION_MUTATION,
  DELETE_COLLECTION_MUTATION,
} from "./graphQL/collection_qry";

import Avatar3 from "./assets/SearchResults/Avatars/Robert_Thompson.png";

//Tooltip Component
const StyledTooltip = ({ title, children }) => (
  <Tooltip
    title={title}
    placement="top"
    arrow
    PopperProps={{
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [0, -5],
          },
        },
      ],
    }}
    componentsProps={{
      tooltip: {
        sx: {
          bgcolor: "#666",
          color: "white",
          fontSize: "12px",
          fontWeight: "500",
          borderRadius: "4px",
          padding: "6px 8px",
          "& .MuiTooltip-arrow": {
            color: "#666",
          },
        },
      },
    }}
  >
    {children}
  </Tooltip>
);

export default function StoryDetail() {
  const { storyUuid } = useParams();

  const navigate = useNavigate();
  const [storyDataObj, setStoryDataObj] = useState({});
  const [storyData, setStoryData] = useState({});
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [commentCount, setCommentCount] = useState(0);
  const [shareCount, setShareCount] = useState(0);
  const [isInCollection, setIsInCollection] = useState(false);
  const [isOwnStory, setIsOwnStory] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [floatMenuLeftMargin, setFloatMenuLeftMargin] = useState(0);
  const [reportDialog, setReportDialog] = useState({ open: false });
  const [shareDialog, setShareDialog] = useState({ open: false });
  const shareUrl = `${window.location.origin}/story/${storyUuid}`;
  const [alertPopup, setAlertPopup] = useState({
    open: false,
  });
  const [isInvalidURL, setIsInvalidURL] = useState(false);
  let role = localStorage.getItem("role") ? localStorage.getItem("role") : null;

  //remove from collection by sami
  const [confirmPopup, setConfirmPopup] = useState({
    open: false,
    title: "",
  });

  //add to collection by sami
  const [successPopup, setSuccessPopup] = useState({
    open: false,
    title: "",
  });

  const [unfollowPopup, setUnfollowPopup] = useState({
    open: false,
    followingName: "",
  });
  const [commentDialog, setCommentDialog] = useState({
    open: false,
    product: null,
  });

  const [createLike] = useMutation(CREATE_STORY_LIKE_MUTATION, {
    awaitRefetchQueries: true,
  });

  const [unLike] = useMutation(DELETE_STORY_LIKE_MUTATION, {
    awaitRefetchQueries: true,
  });

  const [updateStoryShare] = useMutation(UPDATE_STORY_SHARE_NO, {
    awaitRefetchQueries: true,
  });

  const [createComment] = useMutation(CREATE_STORY_COMMENT_MUTATION, {
    awaitRefetchQueries: true,
  });

  const [editComment] = useMutation(EDIT_STORY_COMMENT_MUTATION, {
    awaitRefetchQueries: true,
  });

  const [deleteComment] = useMutation(DELETE_STORY_COMMENT_MUTATION, {
    awaitRefetchQueries: true,
  });

  const [createStoryCommentLike] = useMutation(
    CREATE_STORY_COMMENT_LIKE_MUTATION,
    {
      awaitRefetchQueries: true,
    }
  );

  const [unLikeStoryComment] = useMutation(DELETE_STORY_COMMENT_LIKE_MUTATION, {
    awaitRefetchQueries: true,
  });

  const [createStoryCommentReply] = useMutation(
    CREATE_STORY_COMMENT_REPLY_MUTATION,
    {
      awaitRefetchQueries: true,
    }
  );

  const [editStoryCommentReply] = useMutation(
    EDIT_STORY_COMMENT_REPLY_MUTATION,
    {
      awaitRefetchQueries: true,
    }
  );

  const [deleteStoryCommentReply] = useMutation(
    DELETE_STORY_COMMENT_REPLY_MUTATION,
    {
      awaitRefetchQueries: true,
    }
  );

  const [createStoryCommentReplyLike] = useMutation(
    CREATE_STORY_COMMENT_REPLY_LIKE_MUTATION,
    {
      awaitRefetchQueries: true,
    }
  );

  const [unLikeStoryCommentReply] = useMutation(
    DELETE_STORY_COMMENT_REPLY_LIKE_MUTATION,
    {
      awaitRefetchQueries: true,
    }
  );

  let userUuid = localStorage.getItem("userUuid")
    ? localStorage.getItem("userUuid")
    : null;

  const [getStoryData] = useManualQuery(STORY_BY_UUID, {
    fetchPolicy: "network-only",
  });

  const [createFollow] = useMutation(CREATE_FOLLOW_MUTATION, {
    awaitRefetchQueries: true,
  });

  const [unFollow] = useMutation(DELETE_FOLLOW_MUTATION, {
    awaitRefetchQueries: true,
  });

  //Sami
  const [createCollection] = useMutation(CREATE_COLLECTION_MUTATION, {
    awaitRefetchQueries: true,
  });

  const [deleteCollection] = useMutation(DELETE_COLLECTION_MUTATION, {
    awaitRefetchQueries: true,
  });

  const currentUserName = localStorage.getItem("userName") || "";

  const encodeText = (text) => {
    return encodeURIComponent(text);
  };

  const loadStoryInfo = async () => {
    if (!storyUuid) {
      setIsInvalidURL(true);
      return;
    }
    if (isValidUUIDv4(storyUuid)) {
      const {
        loading: loadingStoryData,
        error: errorStoryData,
        data: storyData,
      } = await getStoryData({
        variables: {
          uuid: storyUuid,
        },
      });
      if (!loadingStoryData && storyData && storyData.storyByUuid) {
        {
          /* check if story is not published and login-user is not owner display error message */
        }
        if (
          role === "Admin" ||
          storyData.storyByUuid.isPublished === true ||
          storyData.storyByUuid.userId.userUuid === userUuid
        ) {
          setStoryDataObj(storyData.storyByUuid);
        } else {
          setAlertPopup({
            open: true,
            title: "This page is not available at this time.",
          });
        }
      } else setIsInvalidURL(true);
      if (errorStoryData) {
        console.log("errorCurrStoryData: ", errorStoryData);
        setIsInvalidURL(true);
      }
    } else setIsInvalidURL(true);
  };

  const setStoryInfo = async () => {
    if (!storyDataObj || !storyDataObj.userId) return;

    setStoryData({
      id: storyDataObj.uuid,
      storyId: storyDataObj.storyId,
      title: storyDataObj.title,
      content: storyDataObj.content,
      img: storyDataObj.fileName,
      userId: {
        userId: storyDataObj.userId.id,
        userUuid: storyDataObj.userId.userUuid,
        firstName: storyDataObj.userId.firstName,
        lastName: storyDataObj.userId.lastName,
        username: storyDataObj.userId.username,
        avatarFileName: storyDataObj.userId.avatarFileName,
        isFollowing: storyDataObj.userId.isLoggedInUserFollowing,
        listings: storyDataObj.userId.listings,
        soldToys: storyDataObj.userId.soldToys,
        ratingNo: storyDataObj.userId.ratingNo,
        ratingCount: storyDataObj.userId.ratingCount,
        dateJoined: storyDataObj.userId.dateJoined,
      },
      date: storyDataObj.publishedDate
        ? dayjs(storyDataObj.publishedDate).format("MMM D, YYYY")
        : "",
      likes: storyDataObj.likeNo,
      shares: storyDataObj.shareNo,
      isFavorited: false,
      isInCollection: storyDataObj.isInCollection,
      comments: storyDataObj.storyComments,
    });

    setCommentCount(storyDataObj.commentNo);
    setIsFollowing(storyDataObj.userId.isLoggedInUserFollowing);
    setIsLiked(storyDataObj.isLiked);
    setLikeCount(storyDataObj.likeNo);
    setShareCount(storyDataObj.shareNo);
    setIsInCollection(storyDataObj.isInCollection);

    // Check if current user is the story author
    setIsOwnStory(storyDataObj.userId.userUuid === userUuid);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    (async () => loadStoryInfo())();
  }, [storyUuid]);

  useEffect(() => {
    (async () => setStoryInfo())();
  }, [storyDataObj]);

  // Set floating menu place based on screen size
  useEffect(() => {
    const handleResize = () => {
      setFloatMenuLeftMargin(window.innerWidth - 100);
    };

    // Set initial value
    handleResize();

    // Add event listener
    window.addEventListener("resize", handleResize);
  }, []);

  const handleLikeToggle = async () => {
    //save story like/unlike in database
    if (!isLiked) {
      const { data, error } = await createLike({
        variables: {
          storyUuid,
        },
      });

      if (error) {
        console.log("Error in create story like", error, data);
      } else {
        console.log("Success in create story like", data);
        setIsLiked(!isLiked);
        setLikeCount((prevCount) => (isLiked ? prevCount - 1 : prevCount + 1));
      }
    } else {
      const { data, error } = await unLike({
        variables: {
          storyUuid,
        },
      });

      if (error) {
        console.log("Error in delete story like", error, data);
      } else {
        console.log("Success in delete story like", data);
        setIsLiked(!isLiked);
        setLikeCount((prevCount) => (isLiked ? prevCount - 1 : prevCount + 1));
      }
    }
  };

  const handleBookmarkClick = () => {
    if (isInCollection) {
      // Show removal confirmation
      setConfirmPopup({
        open: true,
        title: storyData.title,
      });
    } else {
      // Show add alert
      setSuccessPopup({
        open: true,
        title: storyData.title,
      });
    }
  };

  const handleConfirmAdd = async () => {
    await createCollection({
      variables: {
        objectId: parseInt(storyData.storyId),
        contentType: "story",
      },
    });

    setIsInCollection(true);
    setSuccessPopup({ open: false, title: "" });
  };

  const handleConfirmRemove = async () => {
    await deleteCollection({
      variables: {
        objectId: parseInt(storyData.storyId),
        contentType: "story",
      },
    });

    setIsInCollection(false);
    setConfirmPopup({ open: false, title: "" });
  };

  // Handle cancel action
  const handleCancelAction = () => {
    setConfirmPopup({ open: false, title: "" });
    setSuccessPopup({ open: false, title: "" });
  };

  const handleFollowToggle = async () => {
    if (isFollowing) {
      // Show unfollow confirmation popup
      setUnfollowPopup({
        open: true,
        followingName:
          storyData.userId.firstName + " " + storyData.userId.lastName,
      });
    } else {
      //save follow to database
      const { data, error } = await createFollow({
        variables: {
          userUuid: storyData.userId.userUuid,
        },
      });

      if (error) {
        console.log("Error", error, data);
      } else {
        console.log("Success", data);
        // Direct follow action
        setIsFollowing(!isFollowing);
        setStoryData((prev) => ({
          ...prev,
          userId: { ...prev.userId, isFollowing: !isFollowing },
        }));
      }
    }
  };

  const handleConfirmUnfollow = async () => {
    //save unfollow to database
    const { data, error } = await unFollow({
      variables: {
        userUuid: storyData.userId.userUuid,
      },
    });

    if (error) {
      console.log("Error", error, data);
    } else {
      console.log("Success", data);
      setIsFollowing(!isFollowing);
      setUnfollowPopup({ open: false, followingName: "" });
    }
  };

  const handleCancelUnfollow = () => {
    setUnfollowPopup({ open: false, followingName: "" });
  };

  const handleCommentLikeUpdate = async (commentUuid, isLiked) => {
    //save story comment like/unlike in database
    if (isLiked) {
      const { data, error } = await createStoryCommentLike({
        variables: {
          storyCommentUuid: commentUuid,
        },
      });

      if (error) {
        console.log("Error in create story comment like", error, data);
      } else {
        console.log("Success in create story comment like", data);
      }
    } else {
      const { data, error } = await unLikeStoryComment({
        variables: {
          storyCommentUuid: commentUuid,
        },
      });

      if (error) {
        console.log("Error in delete story comment like", error, data);
      } else {
        console.log("Success in delete story comment like", data);
      }
    }
    setStoryData((prev) => ({
      ...prev,
      comments: prev.comments.map((comment) =>
        comment.uuid === commentUuid
          ? {
              ...comment,
              isLiked,
              likeNo: isLiked
                ? comment.likeNo + 1
                : comment.likeNo > 0
                ? comment.likeNo - 1
                : 0,
            }
          : comment
      ),
    }));
  };

  const handleCommentReplyLikeUpdate = async (
    commentUuid,
    commentReplyUuid,
    isLiked
  ) => {
    //save story comment reply like/unlike in database
    if (isLiked) {
      const { data, error } = await createStoryCommentReplyLike({
        variables: {
          storyCommentReplyUuid: commentReplyUuid,
        },
      });

      if (error) {
        console.log("Error in create story comment reply like", error, data);
        return;
      } else {
        console.log("Success in create story comment reply like", data);
      }
    } else {
      const { data, error } = await unLikeStoryCommentReply({
        variables: {
          storyCommentReplyUuid: commentReplyUuid,
        },
      });

      if (error) {
        console.log("Error in delete story comment reply like", error, data);
        return;
      } else {
        console.log("Success in delete story comment reply like", data);
      }
    }

    setStoryData((prev) => ({
      ...prev,
      comments:
        prev.comments &&
        prev.comments.map((comment) =>
          comment.uuid === commentUuid
            ? {
                ...comment,
                replies:
                  comment.replies &&
                  comment.replies.map((commentReply) =>
                    commentReply.uuid === commentReplyUuid
                      ? {
                          ...commentReply,
                          isLiked,
                          likeNo: isLiked
                            ? commentReply.likeNo + 1
                            : commentReply.likeNo > 0
                            ? commentReply.likeNo - 1
                            : 0,
                        }
                      : commentReply
                  ),
              }
            : comment
        ),
    }));
  };

  const handleClose = () => {
    setAlertPopup({ open: false, title: "" });
  };

  const handleCommentClick = () => {
    setCommentDialog({
      open: true,
      product: storyData,
    });
  };

  // Handle cancel removal
  const handleCloseCommentDlg = () => {
    setCommentDialog({ open: false, product: null });
  };

  const handleAddComment = async (comntText) => {
    if (!comntText.trim()) return;
    let commentText = encodeText(comntText);

    //Save story comment in database
    const { data, error } = await createComment({
      variables: {
        storyUuid: storyUuid,
        content: commentText,
      },
    });

    if (error) {
      console.log("Error in creating story comment", error, data);
    } else {
      console.log("Success in creating story comment", data);
      setStoryData((prev) => ({
        ...prev,
        comments: [
          ...(prev.comments || []),
          data.createStoryComment.storyComment,
        ],
      }));
      setCommentCount(commentCount + 1);
    }
  };

  const handleAddReply = async (repText, commentUuid) => {
    if (!repText.trim() || !commentUuid) return;
    let replyText = encodeText(repText);

    //Save story comment reply in database
    const { data, error } = await createStoryCommentReply({
      variables: {
        storyCommentUuid: commentUuid,
        content: replyText,
      },
    });

    if (error) {
      console.log("Error in creating story comment reply", error, data);
      return;
    } else {
      console.log("Success in creating story comment reply", data);
      setStoryData((prev) => ({
        ...prev,
        comments: prev.comments.map((comment) =>
          comment.uuid === commentUuid
            ? {
                ...comment,
                replies: [
                  ...(comment.replies || []),
                  data.createStoryCommentReply.storyCommentReply,
                ],
              }
            : comment
        ),
      }));
    }
  };

  const handleDeleteComment = async (commentUuid) => {
    if (!commentUuid) return;
    const { data, error } = await deleteComment({
      variables: {
        storyCommentUuid: commentUuid,
      },
    });

    if (error) {
      console.log("Error in delete story comment", error, data);
    } else {
      console.log("Success in delete story comment", data);
      setStoryData((prev) => ({
        ...prev,
        comments: prev.comments.filter((c) => c.uuid !== commentUuid),
      }));
      setCommentCount(commentCount - 1);
    }
  };

  const handleDeleteReply = async (commentUuid, replyUuid) => {
    if (!commentUuid || !replyUuid) return;
    const { data, error } = await deleteStoryCommentReply({
      variables: {
        storyCommentReplyUuid: replyUuid,
      },
    });

    if (error) {
      console.log("Error in delete story comment reply", error, data);
    } else {
      console.log("Success in delete story comment reply", data);
      setStoryData((prev) => ({
        ...prev,
        comments: prev.comments.map((comment) =>
          comment.uuid === commentUuid
            ? {
                ...comment,
                replies: comment.replies.filter(
                  (reply) => reply.uuid !== replyUuid
                ),
              }
            : comment
        ),
      }));
    }
  };

  const handleEditComment = async (commentUuid, newEditText) => {
    if (!commentUuid || !newEditText.trim()) return;
    let newText = encodeText(newEditText);
    //Edit story comment in database
    const { data, error } = await editComment({
      variables: {
        storyCommentUuid: commentUuid,
        content: newText,
      },
    });

    if (error) {
      console.log("Error in editing story comment", error, data);
    } else {
      console.log("Success in editing story comment", data);
      setStoryData((prev) => ({
        ...prev,
        comments: prev.comments.map((c) =>
          c.uuid === commentUuid ? { ...c, content: newText } : c
        ),
      }));
    }
  };

  const handleEditReply = async (commentUuid, replyUuid, newEditText) => {
    if (!commentUuid || !replyUuid || !newEditText.trim()) return;
    let newText = encodeText(newEditText);
    //Edit story comment reply in database
    const { data, error } = await editStoryCommentReply({
      variables: {
        storyCommentReplyUuid: replyUuid,
        content: newText,
      },
    });

    if (error) {
      console.log("Error in editing story comment reply", error, data);
    } else {
      console.log("Success in editing story comment reply", data);
      setStoryData((prev) => ({
        ...prev,
        comments: prev.comments.map((comment) =>
          comment.uuid === commentUuid
            ? {
                ...comment,
                replies: comment.replies.map((reply) =>
                  reply.uuid === replyUuid
                    ? { ...reply, content: newText }
                    : reply
                ),
              }
            : comment
        ),
      }));
    }
  };

  // Handle Report Dialog Open Action
  const handleReportDialogOpen = () => {
    setReportDialog({
      open: true,
    });
  };

  // Handle Report Dialog Close action
  const handleReportDialogClose = () => {
    setReportDialog({ open: false });
  };

  // Handle Share Dialog Open Action
  const handleShareDialogOpen = () => {
    setShareDialog({ open: true });
  };

  const handleToyShareSuccess = async () => {
    if (!storyUuid) return;
    const { data, error } = await updateStoryShare({
      variables: {
        storyUuid,
      },
    });

    if (error) {
      console.log("Error in updating story share", error, data);
    } else {
      console.log("Success in updating story share", data);
      setShareCount((prevCount) => prevCount + 1);
    }
  };

  // Handle Share Dialog Close action
  const handleShareDialogClose = () => {
    setShareDialog({ open: false });
  };

  const handleUserProfileNavigation = (user) => {
    // Navigate to user's public homepage
    navigate(`/user/${user.userUuid}/profile`);
  };

  return (
    <>
      {Object.keys(storyData).length > 0 ? (
        <Box sx={{ backgroundColor: "#f5f5f5" }}>
          <Box
            sx={{
              maxWidth: "1440px",
              minHeight: "100vh",
              py: 5,
              px: "100px",
              "@media (max-width: 1360px)": {
                px: "16px",
              },
            }}
          >
            <Helmet>
              <title>R3PLAY - {storyData.title}</title>
              <meta
                name="description"
                content={storyData.content.substring(0, 150)}
              />
            </Helmet>

            {/* Page Header */}
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Typography
                variant="h3"
                sx={{
                  fontSize: "30px",
                  "@media (max-width: 856px)": {
                    fontSize: "24px",
                  },
                  fontWeight: "700",
                  color: "#333",
                  mb: 1,
                }}
              >
                Story
              </Typography>
              <Typography
                sx={{
                  fontSize: "16px",
                  color: "#999",
                  "@media (max-width: 856px)": {
                    fontSize: "14px",
                  },
                }}
              >
                Every toy has a story — share yours with the world.
              </Typography>
            </Box>

            {/* Story Card */}
            <Card
              sx={{
                width: 768,
                margin: "0 auto",
                borderRadius: "12px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                "@media (max-width: 1024px)": {
                  width: 650,
                },
                "@media (max-width: 856px)": {
                  width: 361,
                },
              }}
            >
              <Box>
                {/* User Header */}
                <Box
                  sx={{
                    p: "50px",
                    pb: 3,
                    "@media (max-width: 856px)": {
                      p: 2,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      backgroundColor: "#F5F5F5",
                      borderRadius: "75px",
                      p: 2,
                      height: 72,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <UserCard
                        user={storyData.userId}
                        onUsernameClick={handleUserProfileNavigation}
                        onFullNameClick={handleUserProfileNavigation}
                        type="avatar"
                        context="detail"
                      >
                        <Avatar
                          src={storyData.userId.avatarFileName}
                          sx={{ width: 48, height: 48, cursor: "pointer" }}
                        />
                      </UserCard>
                      <Box>
                        <UserCard
                          user={storyData.userId}
                          onUsernameClick={handleUserProfileNavigation}
                          onFullNameClick={handleUserProfileNavigation}
                          type="username"
                          context="detail"
                        >
                          <Typography
                            variant="body2"
                            fontWeight="medium"
                            sx={{ cursor: "pointer" }}
                          >
                            {storyData.userId.username}
                          </Typography>
                        </UserCard>
                      </Box>
                    </Box>
                    {!isOwnStory && (
                      <FollowButton
                        isFollowing={isFollowing}
                        onToggle={handleFollowToggle}
                      />
                    )}
                  </Box>
                </Box>

                {/* Story Title */}
                <Box
                  sx={{
                    px: "50px",
                    pb: 3,
                    "@media (max-width: 856px)": {
                      px: 2,
                    },
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontSize: "28px",
                      fontWeight: "700",
                      color: "#333",
                      mb: 1,
                      "@media (max-width: 856px)": {
                        fontSize: "24px",
                      },
                    }}
                  >
                    {storyData.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "#999",
                    }}
                  >
                    {storyData.date}
                  </Typography>
                </Box>

                {/* Story Image */}
                {storyData.img && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      px: "50px",
                      "@media (max-width: 856px)": {
                        px: 2,
                      },
                    }}
                  >
                    <CardMedia
                      component="img"
                      image={storyData.img}
                      alt={storyData.title}
                      sx={{
                        width: 668,
                        height: 668,
                        objectFit: "cover",
                        borderRadius: "8px",
                        "@media (max-width: 1024px)": {
                          width: 550,
                          height: 550,
                        },
                        "@media (max-width: 856px)": {
                          width: 329,
                          height: 329,
                        },
                      }}
                    />
                  </Box>
                )}

                {/* Story Content */}
                <CardContent
                  sx={{
                    px: "50px",
                    pt: 3,
                    pb: 3,
                    "@media (max-width: 856px)": {
                      px: 2,
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "16px",
                      lineHeight: 1.8,
                      color: "#333",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    }}
                  >
                    {storyData.content}
                  </Typography>
                </CardContent>
              </Box>
              <Box>
                {/* Divider */}
                <Box
                  sx={{
                    borderTop: "1px solid #e0e0e0",
                    mx: "50px",
                    "@media (max-width: 856px)": {
                      mx: 2,
                    },
                  }}
                />

                {/* Action Buttons */}
                <Box
                  sx={{
                    px: "50px",
                    py: 3,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "nowrap",
                    gap: 1,
                    "@media (max-width: 856px)": {
                      px: 2,
                      gap: 0.5,
                    },
                  }}
                >
                  {/* Left side - Like, Comment, Share buttons */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      flexWrap: "nowrap",
                    }}
                  >
                    <StyledTooltip
                      title={isLiked ? "Click to undo like" : "Click to like"}
                    >
                      <Button
                        onClick={handleLikeToggle}
                        startIcon={
                          isLiked ? (
                            <FavoriteIcon
                              sx={{ fontSize: "18px", color: "#EF5658" }}
                            />
                          ) : (
                            <FavoriteBorderOutlined
                              sx={{ fontSize: "18px", color: "#999" }}
                            />
                          )
                        }
                        sx={{
                          border: "1px solid #ddd",
                          color: "#999",
                          fontSize: "13px",
                          textTransform: "none",
                          height: "32px",
                          px: 1.5,
                          borderRadius: "4px",
                          minWidth: "auto",
                          "@media (max-width: 856px)": {
                            px: 0.5,
                            "& .MuiButton-startIcon": {
                              margin: 0,
                            },
                          },
                          "&:hover": {
                            borderColor: "#EF5658",
                            color: "#EF5658",
                            "& .MuiSvgIcon-root": {
                              color: "#EF5658",
                            },
                          },
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            "@media (max-width: 856px)": {
                              display: "none",
                            },
                          }}
                        >
                          Like
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            ml: 0.5,
                            fontSize: "13px",
                            fontWeight: "500",
                          }}
                        >
                          {likeCount}
                        </Box>
                      </Button>
                    </StyledTooltip>

                    <StyledTooltip title="View/Add comments">
                      <Box onClick={handleCommentClick}>
                        <Button
                          startIcon={
                            <ModeCommentOutlined
                              sx={{ fontSize: "18px", color: "#999" }}
                            />
                          }
                          sx={{
                            border: "1px solid #ddd",
                            color: "#999",
                            fontSize: "13px",
                            textTransform: "none",
                            height: "32px",
                            px: 1.5,
                            borderRadius: "4px",
                            minWidth: "auto",
                            "@media (max-width: 856px)": {
                              px: 0.5,
                              "& .MuiButton-startIcon": {
                                margin: 0,
                              },
                            },
                            "&:hover": {
                              borderColor: "#EF5658",
                              color: "#EF5658",
                              "& .MuiSvgIcon-root": {
                                color: "#EF5658",
                              },
                            },
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              "@media (max-width: 856px)": {
                                display: "none",
                              },
                            }}
                          >
                            Comment
                          </Box>
                          <Box
                            component="span"
                            sx={{
                              ml: 0.5,
                              fontSize: "13px",
                              fontWeight: "500",
                            }}
                          >
                            {commentCount}
                          </Box>
                        </Button>
                      </Box>
                    </StyledTooltip>

                    <StyledTooltip title="Share this story">
                      <Button
                        onClick={handleShareDialogOpen}
                        startIcon={
                          <ShareOutlined
                            sx={{ fontSize: "18px", color: "#999" }}
                          />
                        }
                        sx={{
                          border: "1px solid #ddd",
                          color: "#999",
                          fontSize: "13px",
                          textTransform: "none",
                          height: "32px",
                          px: 1.5,
                          borderRadius: "4px",
                          minWidth: "auto",
                          "@media (max-width: 856px)": {
                            px: 0.5,
                            "& .MuiButton-startIcon": {
                              margin: 0,
                            },
                          },
                          "&:hover": {
                            borderColor: "#EF5658",
                            color: "#EF5658",
                            "& .MuiSvgIcon-root": {
                              color: "#EF5658",
                            },
                          },
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            "@media (max-width: 856px)": {
                              display: "none",
                            },
                          }}
                        >
                          Share
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            ml: 0.5,
                            fontSize: "13px",
                            fontWeight: "500",
                          }}
                        >
                          {shareCount ? shareCount : 0}
                        </Box>
                      </Button>
                    </StyledTooltip>

                    <StyledTooltip
                      title={
                        isInCollection
                          ? "Remove from your collection"
                          : "Add to your collection"
                      }
                    >
                      <Button
                        onClick={handleBookmarkClick}
                        startIcon={
                          isInCollection ? (
                            <CollectionsBookmarkIcon
                              sx={{ fontSize: "18px", color: "#000" }}
                            />
                          ) : (
                            <CollectionsBookmarkOutlinedIcon
                              sx={{ fontSize: "18px", color: "#999" }}
                            />
                          )
                        }
                        sx={{
                          border: "1px solid #ddd",
                          color: "#999",
                          fontSize: "13px",
                          textTransform: "none",
                          height: "32px",
                          px: 1.5,
                          borderRadius: "4px",
                          minWidth: "auto",
                          "@media (max-width: 856px)": {
                            px: 0.5,
                            "& .MuiButton-startIcon": {
                              margin: 0,
                            },
                          },
                          "&:hover": {
                            borderColor: "#EF5658",
                            color: "#EF5658",
                            "& .MuiSvgIcon-root": {
                              color: "#EF5658",
                            },
                          },
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            "@media (max-width: 856px)": {
                              display: "none",
                            },
                          }}
                        >
                          Collection
                        </Box>
                      </Button>
                    </StyledTooltip>
                  </Box>

                  {/* Right side - Report button */}
                  <Box>
                    <StyledTooltip title="Report this story">
                      <Button
                        onClick={handleReportDialogOpen}
                        startIcon={
                          <FlagOutlined
                            sx={{ fontSize: "18px", color: "#999" }}
                          />
                        }
                        sx={{
                          color: "#999",
                          fontSize: "13px",
                          textTransform: "none",
                          height: "32px",
                          borderRadius: "4px",
                          minWidth: "auto",
                          "@media (max-width: 856px)": {
                            "& .MuiButton-startIcon": {
                              margin: 0,
                            },
                          },
                          "&:hover": {
                            backgroundColor: "rgba(239, 86, 88, 0.04)",
                            color: "#EF5658",
                            "& .MuiSvgIcon-root": {
                              color: "#EF5658",
                            },
                          },
                        }}
                      >
                        <Box component="span">Report</Box>
                      </Button>
                    </StyledTooltip>
                  </Box>
                </Box>
              </Box>
            </Card>

            <CommentDialog
              open={commentDialog.open}
              product={storyData}
              comments={
                storyData && storyData.comments ? storyData.comments : []
              }
              onAddComment={(text) => handleAddComment(text)}
              onAddReply={(reply, commentUuid) =>
                handleAddReply(reply, commentUuid)
              }
              onFollowChange={() => handleFollowToggle}
              onCommentLikeToggle={(newLikeState, commentUuid) =>
                handleCommentLikeUpdate(commentUuid, newLikeState)
              }
              onCommentReplyLikeToggle={(
                newLikeState,
                commentUuid,
                commentReplyUuid
              ) =>
                handleCommentReplyLikeUpdate(
                  commentUuid,
                  commentReplyUuid,
                  newLikeState
                )
              }
              onDeleteComment={(commentUuid) =>
                handleDeleteComment(commentUuid)
              }
              onDeleteReply={(commentUuid, replyUuid) =>
                handleDeleteReply(commentUuid, replyUuid)
              }
              onEditComment={(commentUuid, newText) =>
                handleEditComment(commentUuid, newText)
              }
              onEditReply={(commentUuid, replyUuid, newText) =>
                handleEditReply(commentUuid, replyUuid, newText)
              }
              onClose={handleCloseCommentDlg}
            />

            {/* Unfollow Confirmation Popup */}
            <AlertPopup
              open={unfollowPopup.open}
              title="Unfollow This Person"
              message={
                <>
                  Are you sure you want to unfollow user{" "}
                  <strong>{unfollowPopup.followingName}</strong>?
                </>
              }
              confirmText="YES, UNFOLLOW"
              cancelText="CANCEL"
              onConfirm={handleConfirmUnfollow}
              onCancel={handleCancelUnfollow}
              onClose={handleCancelUnfollow}
              showCheckCircle={true}
            />

            {/* Alert Popup for add collection actions */}
            <AlertPopup
              open={successPopup.open}
              title={`Item "${successPopup.title}" is added to your collection.`}
              cancelText=""
              confirmText="OK"
              onConfirm={handleConfirmAdd}
              onClose={() => setSuccessPopup({ open: false, title: "" })}
            />

            {/* Alert Popup for remove collection actions */}
            <AlertPopup
              open={confirmPopup.open}
              title={`Are you sure you want to remove "${confirmPopup.title}" from your collection?`}
              confirmText="Yes, Remove"
              cancelText="Cancel"
              onConfirm={handleConfirmRemove}
              onCancel={() => setConfirmPopup({ open: false, title: "" })}
              onClose={() => setConfirmPopup({ open: false, title: "" })}
            />

            {/* Report Popup */}
            <ReportDialog
              open={reportDialog.open}
              onClose={handleReportDialogClose}
              contentType="story"
              objectId={storyDataObj.storyId}
              objectUuid={storyUuid}
            />

            {/* Share Popup */}
            <SharePopup
              open={shareDialog.open}
              onClose={handleShareDialogClose}
              shareUrl={shareUrl}
              contentTitle={storyDataObj && storyDataObj.title}
              onEmailShareSuccess={handleToyShareSuccess}
            />

            <AlertPopup
              open={alertPopup.open}
              title={alertPopup.title}
              message=""
              confirmText="OK"
              cancelText=""
              onConfirm={handleClose}
              onClose={handleClose}
            />

            <FloatingMenu left={floatMenuLeftMargin} />
          </Box>
        </Box>
      ) : (
        isInvalidURL && (
          <Box sx={{ textAlign: "center", mb: 4, mt: 10 }}>
            <Typography
              variant="h3"
              sx={{
                fontSize: "30px",
                "@media (max-width: 856px)": {
                  fontSize: "24px",
                },
                fontWeight: "700",
                color: "#333",
                mb: 1,
              }}
            >
              This URL is not valid.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate("/dashboard")}
              sx={{
                height: "42px",
                px: 3,
                mt: 5,
                fontWeight: 500,
                backgroundColor: "#EF5658",
                "@media (max-width: 856px)": {
                  fontSize: "13px",
                  height: "30px",
                  px: 1,
                },
              }}
            >
              OK
            </Button>
          </Box>
        )
      )}
    </>
  );
}
